// Volcano Boarding bonus round: ride down Cerro Negro collecting Córdoba coins.
// A can't-lose vertical descent. Steer left/right to grab 100 coins; only the
// count varies. Reaching the bottom ends the round.
import { GAME } from "./config";
import { ready } from "./assets";

const { WIDTH, HEIGHT } = GAME;
const STEP_MS = 1000 / 60; // fixed simulation timestep (frame-rate independent movement)
const TOTAL_COINS = 100;
const COURSE_LEN = 5200; // depth units from crater (0) to town (COURSE_LEN)
const FINISH_LINE = COURSE_LEN - 160; // crossing this ends the run
const PLAYER_SCREEN_Y = 170; // player stays here; world scrolls past

export default class VolcanoGame {
  constructor(canvas, { character, assets, audio, callbacks }) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.ctx.imageSmoothingEnabled = true;
    this.char = character;
    this.assets = assets || {};
    this.audio = audio || null;
    this.cb = callbacks || {};

    this.running = false;
    this.initState();
    this.buildCourse();
    this.bindKeys();
  }

  // Resettable per-run state (shared by constructor and reset()).
  initState() {
    this.input = { left: false, right: false };
    this.ended = false;
    this.paused = true;      // wait for the 3-2-1 countdown before moving
    this.frame = 0;
    this.shake = 0;

    this.px = WIDTH / 2;     // player world-x (screen-x; playfield is full width)
    this.depth = 0;          // how far down the volcano we are
    this.vy = 4.2;           // descent speed (accelerates)
    this.maxVy = 9.6;
    this.wobble = 0;         // brief loss of control after hitting a rock
    this.collected = 0;
    this.trail = [];
    this.sparks = [];
    this.viewOffset = 120; // intro: frame the summit lower so you see the top; eases up on descent
  }

  // Rebuild the run for a fresh attempt (keeps the loop & audio alive).
  reset() {
    this.initState();
    this.buildCourse();
  }

  // Unpause and kick off the descent + music (called after the countdown).
  begin() {
    this.paused = false;
    this.audio?.resume();
    this.audio?.startMusic(3);
  }

  buildCourse() {
    // 100 córdoba coins in steering patterns + scattered ash rocks.
    this.coins = [];
    const startD = 380;
    const span = FINISH_LINE - 360 - startD;
    for (let i = 0; i < TOTAL_COINS; i++) {
      const d = startD + (i / (TOTAL_COINS - 1)) * span;
      // weave the coin path across the slope so the player must steer
      const t = i * 0.5;
      const x = WIDTH / 2 + Math.sin(t * 0.6) * 300 + Math.sin(t * 0.17) * 90;
      this.coins.push({ x: Math.max(60, Math.min(WIDTH - 60, x)), d, taken: false });
    }
    this.rocks = [];
    for (let i = 0; i < 16; i++) {
      const d = 900 + i * ((COURSE_LEN - 1600) / 16) + (Math.random() - 0.5) * 120;
      const x = 90 + Math.random() * (WIDTH - 180);
      this.rocks.push({ x, d, r: 22 + Math.random() * 14 });
    }
  }

  bindKeys() {
    this._onKey = (e, down) => {
      const c = e.code;
      if (c === "ArrowLeft" || c === "KeyA") this.input.left = down;
      else if (c === "ArrowRight" || c === "KeyD") this.input.right = down;
      else return;
      e.preventDefault();
    };
    this._kd = (e) => this._onKey(e, true);
    this._ku = (e) => this._onKey(e, false);
    window.addEventListener("keydown", this._kd);
    window.addEventListener("keyup", this._ku);
  }
  setInput(name, val) { if (name === "left" || name === "right") this.input[name] = val; }

  start() {
    if (this.running) return;
    this.running = true;
    this.loop = this.loop.bind(this);
    this.raf = requestAnimationFrame(this.loop);
  }
  destroy() {
    this.running = false;
    cancelAnimationFrame(this.raf);
    this.audio?.stopMusic();
    window.removeEventListener("keydown", this._kd);
    window.removeEventListener("keyup", this._ku);
  }
  loop() {
    if (!this.running) return;
    const now = performance.now();
    if (!this.ended && !this.paused) {
      // Fixed-timestep accumulator: keeps game speed consistent regardless of
      // device frame rate (slow phones no longer run in slow-motion).
      let frame = this._lastTs != null ? now - this._lastTs : STEP_MS;
      if (frame > 250) frame = 250; // avoid spiral-of-death after tab sleep
      this._acc = (this._acc || 0) + frame;
      let steps = 0;
      while (this._acc >= STEP_MS && steps < 5) { this.update(); this._acc -= STEP_MS; steps++; }
    }
    this._lastTs = now;
    this.render();
    this.raf = requestAnimationFrame(this.loop);
  }

  end() {
    if (this.ended) return;
    this.ended = true;
    this.audio?.stopMusic();
    this.audio?.sfx("win");
    this.cb.onComplete && this.cb.onComplete({ collected: this.collected, total: TOTAL_COINS });
  }

  update() {
    this.frame++;
    if (this.shake > 0) this.shake--;
    if (this.wobble > 0) this.wobble--;
    // ease the intro camera back up once the descent begins
    if (this.viewOffset > 0.5) this.viewOffset *= 0.965; else this.viewOffset = 0;

    // accelerate the descent
    this.vy = Math.min(this.maxVy, this.vy + 0.007);
    this.depth += this.vy;

    // steering (reduced control while wobbling)
    const steer = (this.wobble > 0 ? 2.8 : 6.8);
    if (this.input.left) this.px -= steer;
    if (this.input.right) this.px += steer;
    this.px = Math.max(44, Math.min(WIDTH - 44, this.px));

    // dust trail behind board
    if (this.frame % 2 === 0) {
      this.trail.push({ x: this.px + (Math.random() - 0.5) * 18, y: PLAYER_SCREEN_Y + (this.viewOffset || 0) + 26, life: 22, size: 5 + Math.random() * 5 });
    }
    for (const tr of this.trail) { tr.y += 1.4; tr.life--; }
    this.trail = this.trail.filter((tr) => tr.life > 0);
    for (const s of this.sparks) { s.x += s.vx; s.y += s.vy; s.vy += 0.25; s.life--; }
    this.sparks = this.sparks.filter((s) => s.life > 0);

    // coin collection
    for (const c of this.coins) {
      if (c.taken) continue;
      if (Math.abs(c.d - this.depth) < 26 && Math.abs(c.x - this.px) < 34) {
        c.taken = true; this.collected++; this.audio?.sfx("coin");
        for (let k = 0; k < 8; k++) {
          const a = Math.random() * Math.PI * 2, sp = 1 + Math.random() * 3;
          this.sparks.push({ x: c.x, y: PLAYER_SCREEN_Y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 2, life: 24, color: "#ffd24a" });
        }
      }
    }
    // rock bumps (gentle wobble, no coin loss)
    for (const r of this.rocks) {
      if (!r.hit && Math.abs(r.d - this.depth) < 22 && Math.abs(r.x - this.px) < r.r + 22) {
        r.hit = true; this.wobble = 40; this.shake = 12; this.vy = Math.max(3.4, this.vy * 0.6); this.audio?.sfx("hurt");
      }
    }

    this.cb.onProgress && this.cb.onProgress({ collected: this.collected, total: TOTAL_COINS, pct: Math.min(100, (this.depth / FINISH_LINE) * 100) });

    if (this.depth >= FINISH_LINE) this.end();
  }

  // ---------------- Render ----------------
  screenY(d) { return PLAYER_SCREEN_Y + (d - this.depth) + (this.viewOffset || 0); }

  render() {
    const ctx = this.ctx;
    ctx.save();
    if (this.shake > 0) ctx.translate((Math.random() - 0.5) * this.shake, (Math.random() - 0.5) * this.shake);

    this.drawSlope();

    // crater smoke at the very top of the course
    const craterY = this.screenY(0);
    if (craterY > -200 && craterY < HEIGHT) this.drawCrater(craterY);
    // finish banner near the bottom of the course
    const finishY = this.screenY(FINISH_LINE);
    if (finishY > -40 && finishY < HEIGHT + 80) this.drawFinish(finishY);

    // rocks — light boulders with a pulsing warning ring + shadow so they pop
    // against the dark ash slope
    for (const r of this.rocks) {
      const y = this.screenY(r.d);
      if (y < -50 || y > HEIGHT + 50) continue;
      const hit = r.hit;
      // ground shadow
      ctx.fillStyle = "rgba(0,0,0,0.42)";
      ctx.beginPath(); ctx.ellipse(r.x, y + r.r * 0.7, r.r * 1.05, r.r * 0.42, 0, 0, Math.PI * 2); ctx.fill();
      // pulsing orange danger ring (only while still a threat)
      if (!hit) {
        const pulse = 0.5 + 0.5 * Math.sin(this.frame * 0.18 + r.x);
        ctx.strokeStyle = `rgba(255,${120 + pulse * 80},40,${0.45 + pulse * 0.4})`;
        ctx.lineWidth = 4;
        ctx.beginPath(); ctx.ellipse(r.x, y, r.r + 9, r.r * 0.82 + 9, 0, 0, Math.PI * 2); ctx.stroke();
      }
      // boulder body (warm light gray) with dark outline
      ctx.lineWidth = 3; ctx.strokeStyle = "#0c0c10";
      ctx.fillStyle = hit ? "#4a4a50" : "#837e79";
      ctx.beginPath(); ctx.ellipse(r.x, y, r.r, r.r * 0.82, 0, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      // top highlight
      ctx.fillStyle = hit ? "#5e5e64" : "#aaa39c";
      ctx.beginPath(); ctx.ellipse(r.x - r.r * 0.28, y - r.r * 0.32, r.r * 0.5, r.r * 0.32, 0, 0, Math.PI * 2); ctx.fill();
      // crack detail
      ctx.strokeStyle = "rgba(0,0,0,0.35)"; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(r.x - r.r * 0.4, y - r.r * 0.05); ctx.lineTo(r.x + r.r * 0.3, y + r.r * 0.22); ctx.stroke();
    }

    // coins (córdoba)
    for (const c of this.coins) {
      if (c.taken) continue;
      const y = this.screenY(c.d);
      if (y < -30 || y > HEIGHT + 30) continue;
      this.drawCoin(c.x, y);
    }

    // dust trail
    for (const tr of this.trail) {
      ctx.globalAlpha = Math.min(0.5, tr.life / 30); ctx.fillStyle = "#6b6b72";
      ctx.beginPath(); ctx.arc(tr.x, tr.y, tr.size, 0, Math.PI * 2); ctx.fill(); ctx.globalAlpha = 1;
    }

    // player on board
    this.drawBoarder();

    // sparks
    for (const s of this.sparks) {
      ctx.globalAlpha = Math.min(1, s.life / 20); ctx.fillStyle = s.color;
      ctx.fillRect(Math.round(s.x), Math.round(s.y), 4, 4); ctx.globalAlpha = 1;
    }

    // side speed lines
    this.drawSpeedLines();

    ctx.restore();
  }

  drawSlope() {
    const ctx = this.ctx;
    const img = this.assets.volcano_slope;
    if (ready(img)) {
      // Tile the real ash photo down the slope, mirroring every other tile so
      // repeating seams disappear, scrolling it with our descent depth.
      const drawH = WIDTH * (img.naturalHeight / img.naturalWidth);
      const i0 = Math.floor((this.depth - PLAYER_SCREEN_Y) / drawH) - 1;
      for (let i = i0; ; i++) {
        const sy = PLAYER_SCREEN_Y + (i * drawH - this.depth);
        if (sy > HEIGHT) break;
        if (((i % 2) + 2) % 2 === 1) {
          ctx.save(); ctx.translate(0, sy + drawH); ctx.scale(1, -1);
          ctx.drawImage(img, 0, 0, WIDTH, drawH); ctx.restore();
        } else {
          ctx.drawImage(img, 0, sy, WIDTH, drawH);
        }
      }
      // cinematic side vignette
      const vg = ctx.createLinearGradient(0, 0, WIDTH, 0);
      vg.addColorStop(0, "rgba(0,0,0,0.38)"); vg.addColorStop(0.5, "rgba(0,0,0,0)"); vg.addColorStop(1, "rgba(0,0,0,0.38)");
      ctx.fillStyle = vg; ctx.fillRect(0, 0, WIDTH, HEIGHT);
      return;
    }
    // ---- fallback: drawn ash gradient ----
    const g = ctx.createLinearGradient(0, 0, 0, HEIGHT);
    g.addColorStop(0, "#3a3531");
    g.addColorStop(0.5, "#211e1c");
    g.addColorStop(1, "#161311");
    ctx.fillStyle = g; ctx.fillRect(0, 0, WIDTH, HEIGHT);
    ctx.strokeStyle = "rgba(255,255,255,0.05)"; ctx.lineWidth = 2;
    const step = 46;
    const off = (this.depth * 0.9) % step;
    for (let y = -step + off; y < HEIGHT + step; y += step) {
      ctx.beginPath();
      ctx.moveTo(0, y); ctx.lineTo(WIDTH, y + 10); ctx.stroke();
    }
  }

  drawCrater(y) {
    const ctx = this.ctx;
    const cx = WIDTH / 2;
    const rw = 240, rh = 78;
    const pulse = 0.5 + 0.5 * Math.sin(this.frame * 0.08);

    // --- sky + volcano cone summit (visible near the top of the course) ---
    if (y > -20) {
      const sky = ctx.createLinearGradient(0, 0, 0, Math.max(1, y + 40));
      sky.addColorStop(0, "#241633");
      sky.addColorStop(1, "#b14a26");
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, WIDTH, Math.max(0, y + 40));
      // dark volcanic cone with a notched crater rim at the summit
      const cone = ctx.createLinearGradient(0, y - 60, 0, y + 140);
      cone.addColorStop(0, "#2a1c16");
      cone.addColorStop(1, "#130c09");
      ctx.fillStyle = cone;
      ctx.beginPath();
      ctx.moveTo(-60, y + 160);
      ctx.lineTo(cx - rw * 0.92, y + 6);
      ctx.quadraticCurveTo(cx, y - 30, cx + rw * 0.92, y + 6);
      ctx.lineTo(WIDTH + 60, y + 160);
      ctx.closePath();
      ctx.fill();
      // glowing lava streaks running down the cone
      ctx.lineWidth = 4; ctx.lineCap = "round";
      ctx.strokeStyle = `rgba(255,${90 + pulse * 70},30,0.85)`;
      for (const s of [-0.6, -0.25, 0.2, 0.55]) {
        ctx.beginPath();
        ctx.moveTo(cx + rw * s * 0.7, y + 4);
        ctx.lineTo(cx + rw * s, y + 78 + Math.abs(s) * 46);
        ctx.stroke();
      }
      ctx.lineCap = "butt";
    }

    // --- glowing crater mouth ---
    const glow = ctx.createRadialGradient(cx, y, 8, cx, y, rw);
    glow.addColorStop(0, `rgba(255,170,40,${0.55 + pulse * 0.25})`);
    glow.addColorStop(0.5, "rgba(255,90,20,0.22)");
    glow.addColorStop(1, "rgba(255,90,20,0)");
    ctx.fillStyle = glow;
    ctx.beginPath(); ctx.ellipse(cx, y, rw, rh, 0, 0, Math.PI * 2); ctx.fill();

    const img = this.assets.volcano_crater;
    if (ready(img)) {
      ctx.save();
      ctx.beginPath(); ctx.ellipse(cx, y, rw * 0.8, rh, 0, 0, Math.PI * 2); ctx.clip();
      const w = rw * 1.7, h = w * (img.naturalHeight / img.naturalWidth);
      ctx.drawImage(img, cx - w / 2, y - h / 2, w, h);
      ctx.restore();
    } else {
      ctx.fillStyle = "#1a1210"; ctx.beginPath(); ctx.ellipse(cx, y, rw * 0.8, rh, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#ff5722"; ctx.beginPath(); ctx.ellipse(cx, y, rw * 0.5, rh * 0.55, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = `rgba(255,210,74,${0.7 + pulse * 0.3})`; ctx.beginPath(); ctx.ellipse(cx, y, rw * 0.28, rh * 0.32, 0, 0, Math.PI * 2); ctx.fill();
    }
    // dark rocky rim
    ctx.strokeStyle = "#0d0907"; ctx.lineWidth = 9;
    ctx.beginPath(); ctx.ellipse(cx, y, rw * 0.8, rh, 0, 0, Math.PI * 2); ctx.stroke();

    // smoke puffs rising from the crater
    ctx.fillStyle = "rgba(120,120,128,0.5)";
    for (let i = 0; i < 5; i++) {
      const px = cx + Math.sin(this.frame * 0.02 + i) * 60;
      const py = y - 40 - i * 34 - (this.frame * 0.3) % 34;
      ctx.beginPath(); ctx.arc(px, py, 26 + i * 6, 0, Math.PI * 2); ctx.fill();
    }
  }

  drawFinish(y) {
    const ctx = this.ctx;
    const img = this.assets.volcano_town;
    if (ready(img)) {
      // Real green plains / town photo where the ash meets the valley floor.
      const h = HEIGHT - y;
      if (h > 0) {
        const drawH = Math.max(h, WIDTH * (img.naturalHeight / img.naturalWidth));
        ctx.drawImage(img, 0, y, WIDTH, drawH);
      }
    } else {
      ctx.fillStyle = "#4CAF50"; ctx.fillRect(0, y + 14, WIDTH, Math.max(0, HEIGHT - (y + 14)));
    }
    // checkered finish banner
    ctx.fillStyle = "#0067C6"; ctx.fillRect(0, y, WIDTH, 14);
    ctx.fillStyle = "#fff";
    for (let x = 0; x < WIDTH; x += 28) ctx.fillRect(x, y, 14, 14);
  }

  drawCoin(x, y) {
    const ctx = this.ctx;
    const spin = Math.abs(Math.sin(this.frame * 0.12 + x * 0.05));
    const rx = 13 * (0.35 + spin * 0.65);
    ctx.save();
    ctx.shadowColor = "rgba(255,210,74,0.7)"; ctx.shadowBlur = 10;
    ctx.fillStyle = "#f2b701"; ctx.beginPath(); ctx.ellipse(x, y, rx, 14, 0, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = "#ffe17a"; ctx.beginPath(); ctx.ellipse(x, y, rx * 0.7, 10, 0, 0, Math.PI * 2); ctx.fill();
    if (rx > 7) {
      ctx.fillStyle = "#9a6b00"; ctx.font = "800 12px 'Baloo 2', system-ui, sans-serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText("C$", x, y + 1);
    }
    ctx.restore();
  }

  drawBoarder() {
    const ctx = this.ctx;
    const x = this.px, y = PLAYER_SCREEN_Y + (this.viewOffset || 0);
    const tilt = (this.input.right ? 0.18 : this.input.left ? -0.18 : 0) + (this.wobble > 0 ? Math.sin(this.frame * 0.6) * 0.25 : 0);
    ctx.save();
    ctx.translate(x, y + 28);
    ctx.rotate(tilt);
    // board
    ctx.fillStyle = "#7a4a2a"; ctx.beginPath(); ctx.ellipse(0, 11, 46, 12, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#9a6b3a"; ctx.fillRect(-42, 6, 84, 6);
    ctx.restore();

    // character sprite, facing down the hill
    const img = this.assets[this.char.id];
    if (ready(img)) {
      const targetH = 120;
      const scale = targetH / img.naturalHeight;
      const w = img.naturalWidth * scale;
      ctx.save();
      ctx.translate(x, y + 34);
      ctx.rotate(tilt * 0.6);
      ctx.drawImage(img, -w / 2, -targetH, w, targetH);
      ctx.restore();
    } else {
      ctx.fillStyle = this.char.colors.shirt; ctx.fillRect(x - 17, y - 34, 34, 48);
      ctx.fillStyle = this.char.colors.skin; ctx.fillRect(x - 14, y - 57, 28, 26);
    }
  }

  drawSpeedLines() {
    const ctx = this.ctx;
    ctx.strokeStyle = "rgba(255,255,255,0.18)"; ctx.lineWidth = 3;
    const n = 6;
    for (let i = 0; i < n; i++) {
      const side = i < n / 2 ? 1 : -1;
      const baseX = side > 0 ? 30 + (i % 3) * 22 : WIDTH - 30 - (i % 3) * 22;
      const y = ((i * 90 + this.frame * (this.vy * 2.2)) % (HEIGHT + 80)) - 40;
      ctx.beginPath(); ctx.moveTo(baseX, y); ctx.lineTo(baseX, y + 40); ctx.stroke();
    }
  }
}
